/**
 * Intelligence & Reporting — Centralized analytics dashboards
 * covering overrides, revenue leakage, operational risk,
 * lead source intelligence, and sales performance.
 */

import { useState, useMemo, useEffect } from "react";
import { isWonStatus } from "@/data/quoteTypes";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useSafetyControl } from "@/data/SafetyControlContext";
import { useRole } from "@/data/RoleContext";
import { getJobHealth } from "@/data/jobHealth";
import type { IntakeRecord } from "@/data/leadIntegrityEngine";
import { LeadIntegrityService } from "@/services/leadIntegrity.service";
import { getReadinessScore } from "@/components/JobWorkflowStatus";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  ShieldAlert, DollarSign, AlertTriangle, Megaphone, Target,
  ArrowRight, TrendingDown, TrendingUp, Ban, CheckCircle, XCircle,
  Users, BarChart3, Percent, Clock, FileWarning, Activity,
} from "lucide-react";
import type { Job } from "@/data/types";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";

const C = {
  primary: "hsl(var(--chart-1))",
  success: "hsl(var(--chart-2))",
  warning: "hsl(var(--chart-5))",
  destructive: "hsl(var(--destructive))",
  accent: "hsl(var(--chart-3))",
  info: "hsl(var(--info))",
  muted: "hsl(var(--muted-foreground))",
};
const PIE = [C.primary, C.success, C.warning, C.destructive, C.accent, C.info, C.muted];

function getJobDisplayRef(job: Pick<Job, "id" | "jobNumber">): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function MetricCard({ label, value, sub, icon: Icon, color = "text-foreground", link }: {
  label: string; value: string | number; sub?: string; icon: React.ElementType; color?: string; link?: string;
}) {
  const inner = (
    <Card className={`${link ? "hover:border-primary/40 transition-colors cursor-pointer" : ""}`}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className="rounded-lg bg-muted/60 p-2"><Icon className={`h-4 w-4 ${color}`} /></div>
        <div className="min-w-0 flex-1">
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          <p className="text-[11px] text-muted-foreground truncate">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground/70">{sub}</p>}
        </div>
        {link && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mt-1" />}
      </CardContent>
    </Card>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}

/* ═══════════════════════════════════════════════════════════════
   OVERRIDE INTELLIGENCE
   ═══════════════════════════════════════════════════════════════ */

export function OverrideIntelligence() {
  const { overrideLog, rules } = useSafetyControl();

  const byUser = useMemo(() => {
    const map = new Map<string, { user: string; role: string; count: number; overridden: number; blocked: number }>();
    overrideLog.forEach(e => {
      const k = e.user || "Unknown";
      const ex = map.get(k) || { user: k, role: e.role, count: 0, overridden: 0, blocked: 0 };
      ex.count++;
      if (e.outcome === "overridden") ex.overridden++;
      if (e.outcome === "blocked") ex.blocked++;
      map.set(k, ex);
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [overrideLog]);

  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    overrideLog.forEach(e => {
      const rule = rules.find(r => r.code === e.ruleCode);
      const cat = rule?.category || "unknown";
      map.set(cat, (map.get(cat) || 0) + 1);
    });
    return [...map.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [overrideLog, rules]);

  const byOutcome = useMemo(() => {
    const counts = { overridden: 0, blocked: 0, passed: 0 };
    overrideLog.forEach(e => { counts[e.outcome] = (counts[e.outcome] || 0) + 1; });
    return [
      { name: "Overridden", value: counts.overridden },
      { name: "Blocked", value: counts.blocked },
      { name: "Passed", value: counts.passed },
    ].filter(d => d.value > 0);
  }, [overrideLog]);

  if (overrideLog.length === 0) {
    return (
      <Card><CardContent className="p-8 text-center text-muted-foreground">
        <ShieldAlert className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">No Override Data Yet</p>
        <p className="text-xs">Override events will appear here as users interact with validation gates.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Events" value={overrideLog.length} icon={Activity} />
        <MetricCard label="Overridden" value={overrideLog.filter(e => e.outcome === "overridden").length} icon={ShieldAlert} color="text-warning" />
        <MetricCard label="Blocked" value={overrideLog.filter(e => e.outcome === "blocked").length} icon={Ban} color="text-destructive" />
        <MetricCard label="Passed Clean" value={overrideLog.filter(e => e.outcome === "passed").length} icon={CheckCircle} color="text-success" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Outcome Distribution */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Outcome Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={byOutcome} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {byOutcome.map((_, i) => <Cell key={i} fill={[C.warning, C.destructive, C.success][i] || C.muted} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By Category */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Events by Category</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={byCategory} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill={C.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* By User */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Overrides by User</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">User</TableHead>
                <TableHead className="text-xs">Role</TableHead>
                <TableHead className="text-xs text-right">Total</TableHead>
                <TableHead className="text-xs text-right">Overridden</TableHead>
                <TableHead className="text-xs text-right">Blocked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byUser.map(u => (
                <TableRow key={u.user}>
                  <TableCell className="text-xs font-medium">{u.user}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px] capitalize">{u.role.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-xs text-right">{u.count}</TableCell>
                  <TableCell className="text-xs text-right text-warning">{u.overridden}</TableCell>
                  <TableCell className="text-xs text-right text-destructive">{u.blocked}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   REVENUE LEAKAGE
   ═══════════════════════════════════════════════════════════════ */

export function RevenueLeakage() {
  const { quotes, jobs, credits } = useAppData();

  const metrics = useMemo(() => {
    const wonQuotes = quotes.filter(q => isWonStatus(q.status));
    const lostQuotes = quotes.filter(q => q.status === "Closed Lost");
    const belowTarget = wonQuotes.filter(q => q.pricingStatus === "Below Target" || q.pricingStatus === "Below Floor");
    const belowFloor = wonQuotes.filter(q => q.pricingStatus === "Below Floor");
    const withOverride = wonQuotes.filter(q => q.pricingOverride);
    const withDiscount = wonQuotes.filter(q => q.pricingOverride && q.pricingOverride.variance < 0);
    const totalRevenue = wonQuotes.reduce((s, q) => s + (q.totalEstimate || 0), 0);
    const avgDeal = wonQuotes.length > 0 ? totalRevenue / wonQuotes.length : 0;

    // Cancellations by reason
    const cancelReasons = new Map<string, number>();
    lostQuotes.forEach(q => {
      const reason = q.lostReason || "Not specified";
      cancelReasons.set(reason, (cancelReasons.get(reason) || 0) + 1);
    });

    // Unpaid completed jobs
    const completedJobs = jobs.filter(j => j.currentStage === "Completed");
    const paidStatuses = new Set(["paid", "paid in full"]);
    const unpaidJobs = completedJobs.filter((j) => {
      const paymentStatus = String(j.financials?.paymentStatus ?? "").trim().toLowerCase();
      if (!paymentStatus) return true;
      return !paidStatuses.has(paymentStatus);
    });

    // Credits / change orders
    const totalCredits = credits.reduce((s, c) => s + c.creditAmount, 0);

    return {
      totalRevenue,
      avgDeal,
      belowTargetCount: belowTarget.length,
      belowFloorCount: belowFloor.length,
      overrideCount: withOverride.length,
      discountCount: withDiscount.length,
      avgDiscount: withDiscount.length > 0
        ? Math.abs(withDiscount.reduce((s, q) => s + (q.pricingOverride?.variance || 0), 0) / withDiscount.length / 100)
        : 0,
      lostCount: lostQuotes.length,
      cancelReasons: [...cancelReasons.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
      unpaidCount: unpaidJobs.length,
      totalCredits,
      creditCount: credits.length,
    };
  }, [quotes, jobs, credits]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Revenue (Won)" value={`$${(metrics.totalRevenue / 1000).toFixed(0)}k`} icon={DollarSign} color="text-success" />
        <MetricCard label="Below Target Quotes" value={metrics.belowTargetCount} sub={`${metrics.belowFloorCount} below floor`} icon={TrendingDown} color="text-warning" link="/opportunities" />
        <MetricCard label="Avg Discount Applied" value={`${metrics.avgDiscount.toFixed(1)}%`} sub={`${metrics.discountCount} deals with discounts`} icon={Percent} color="text-warning" />
        <MetricCard label="Unpaid Completed Jobs" value={metrics.unpaidCount} icon={FileWarning} color="text-destructive" link="/jobs" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <MetricCard label="Lost Quotes" value={metrics.lostCount} icon={XCircle} color="text-destructive" link="/opportunities" />
        <MetricCard label="Total Credits Issued" value={`$${metrics.totalCredits.toLocaleString()}`} sub={`${metrics.creditCount} credits`} icon={DollarSign} color="text-warning" />
        <MetricCard label="Avg Deal Size" value={`$${metrics.avgDeal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} icon={Target} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cancellation Reasons */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Lost Quotes by Reason</CardTitle></CardHeader>
          <CardContent>
            {metrics.cancelReasons.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No lost quotes recorded.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={metrics.cancelReasons}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill={C.destructive} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Pricing Distribution */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Pricing Health (Won Quotes)</CardTitle></CardHeader>
          <CardContent>
            {(() => {
              const wonQuotes = quotes.filter(q => isWonStatus(q.status));
              const data = [
                { name: "Within Target", value: wonQuotes.filter(q => q.pricingStatus === "Within Target" || !q.pricingStatus).length },
                { name: "Below Target", value: wonQuotes.filter(q => q.pricingStatus === "Below Target").length },
                { name: "Below Floor", value: wonQuotes.filter(q => q.pricingStatus === "Below Floor").length },
                { name: "Premium", value: wonQuotes.filter(q => q.pricingStatus === "Premium").length },
              ].filter(d => d.value > 0);
              return (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {data.map((_, i) => <Cell key={i} fill={[C.success, C.warning, C.destructive, C.primary][i] || C.muted} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   OPERATIONAL RISK
   ═══════════════════════════════════════════════════════════════ */

export function OperationalRisk() {
  const { jobs, issues, credits, buildReviewEvents } = useAppData();

  const metrics = useMemo(() => {
    const activeJobs = jobs.filter(j => j.currentStage !== "Completed");
    const jobHealthMap = activeJobs.map(j => {
      const jobIssues = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      const jobCredits = credits.filter(c => c.jobId === j.id);
      const health = getJobHealth(j, jobIssues, jobCredits);
      return { job: j, health };
    });
    const atRiskJobs = jobHealthMap.filter(jh => jh.health.status === "At Risk" || jh.health.status === "Problem");
    const missingPrereqs = activeJobs.filter(j => {
      const score = getReadinessScore(j, issues.filter(i => i.jobId === j.id), buildReviewEvents);
      return score < 60;
    });
    const openIssues = issues.filter(i => i.status === "Open" || i.status === "In Progress");
    const urgentIssues = openIssues.filter(i => i.severity === "Urgent");

    // QA failures
    const qaIssues = issues.filter(i => i.source === "QA Inspector");
    const qaFailRate = jobs.length > 0 ? (qaIssues.length / jobs.length * 100) : 0;

    // Issues by contractor
    const contractorIssues = new Map<string, number>();
    issues.forEach(i => {
      const job = jobs.find(j => j.id === i.jobId);
      const assignee = job?.assignedSubcontractor || job?.assignedCrew || "Unassigned";
      contractorIssues.set(assignee, (contractorIssues.get(assignee) || 0) + 1);
    });
    const topContractorIssues = [...contractorIssues.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // Delayed installs
    const delayed = activeJobs.filter(j => {
      const date = j.scheduledInstallDate || j.targetInstallDate;
      if (!date) return false;
      return new Date(date) < new Date() && j.currentStage !== "In Progress" && j.currentStage !== "QA Review";
    });

    return {
      activeCount: activeJobs.length,
      atRiskCount: atRiskJobs.length,
      missingPrereqs: missingPrereqs.length,
      openIssues: openIssues.length,
      urgentIssues: urgentIssues.length,
      qaFailRate,
      delayedCount: delayed.length,
      topContractorIssues,
      atRiskJobs: atRiskJobs.slice(0, 10),
    };
  }, [jobs, issues, credits]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="At-Risk Jobs" value={metrics.atRiskCount} icon={AlertTriangle} color="text-destructive" link="/jobs" />
        <MetricCard label="Missing Prerequisites" value={metrics.missingPrereqs} icon={FileWarning} color="text-warning" link="/jobs" />
        <MetricCard label="Urgent Issues" value={metrics.urgentIssues} sub={`${metrics.openIssues} total open`} icon={ShieldAlert} color="text-destructive" link="/issues" />
        <MetricCard label="Delayed Installs" value={metrics.delayedCount} icon={Clock} color="text-warning" link="/scheduling" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Issues by Contractor */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Issues by Contractor / Crew</CardTitle></CardHeader>
          <CardContent>
            {metrics.topContractorIssues.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No contractor issues.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={metrics.topContractorIssues} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={C.destructive} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* At-Risk Jobs */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">At-Risk Jobs</CardTitle></CardHeader>
          <CardContent className="p-0">
            {metrics.atRiskJobs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No at-risk jobs.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Job</TableHead>
                    <TableHead className="text-xs">Stage</TableHead>
                    <TableHead className="text-xs">Health</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.atRiskJobs.map(jh => (
                    <TableRow key={jh.job.id}>
                      <TableCell className="text-xs">
                        <Link to={`/jobs/${jh.job.id}`} className="text-primary hover:underline font-medium">{getJobDisplayRef(jh.job)}</Link>
                        <p className="text-[10px] text-muted-foreground truncate">{jh.job.customerName}</p>
                      </TableCell>
                      <TableCell className="text-xs">{jh.job.currentStage}</TableCell>
                      <TableCell>
                        <Badge variant={jh.health.status === "Problem" ? "destructive" : "default"} className="text-[10px]">
                          {jh.health.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   LEAD SOURCE INTELLIGENCE (Compact)
   ═══════════════════════════════════════════════════════════════ */

export function LeadSourceSummary() {
  const { quotes, leads } = useAppData();
  const [intakeRecords, setIntakeRecords] = useState<IntakeRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await LeadIntegrityService.getAll();
      if (cancelled) return;
      setIntakeRecords(data ?? []);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const data = useMemo(() => {
    const sources = new Map<string, {
      total: number; approved: number; rejected: number; duplicates: number;
      quotes: number; won: number; revenue: number;
    }>();

    const intakeCreatedLeadIds = new Set(
      intakeRecords
        .map((record) => String(record.createdLeadId ?? "").trim())
        .filter(Boolean),
    );

    intakeRecords.forEach(rec => {
      const src = rec.normalizedData.leadSource || "Unknown";
      const e = sources.get(src) || { total: 0, approved: 0, rejected: 0, duplicates: 0, quotes: 0, won: 0, revenue: 0 };
      e.total++;
      if (rec.disposition === "approved" || rec.disposition === "approved_with_warning") e.approved++;
      if (rec.disposition === "rejected") e.rejected++;
      if (rec.disposition === "duplicate_flagged") e.duplicates++;
      sources.set(src, e);
    });

    leads.forEach(l => {
      const src = l.leadSource || "Direct";
      const e = sources.get(src) || { total: 0, approved: 0, rejected: 0, duplicates: 0, quotes: 0, won: 0, revenue: 0 };
      if (!intakeCreatedLeadIds.has(String(l.id ?? "").trim())) e.total++;
      sources.set(src, e);
    });

    quotes.forEach(q => {
      const src = q.leadSource || "Direct / Unknown";
      const e = sources.get(src) || { total: 0, approved: 0, rejected: 0, duplicates: 0, quotes: 0, won: 0, revenue: 0 };
      e.quotes++;
      if (isWonStatus(q.status)) { e.won++; e.revenue += q.totalEstimate || 0; }
      sources.set(src, e);
    });

    return [...sources.entries()].map(([source, d]) => ({
      source,
      ...d,
      rejectRate: d.total > 0 ? Math.round((d.rejected / d.total) * 100) : 0,
      convRate: d.quotes > 0 ? Math.round((d.won / d.quotes) * 100) : 0,
    })).sort((a, b) => b.total - a.total);
  }, [quotes, leads, intakeRecords]);

  const totalLeads = data.reduce((s, d) => s + d.total, 0);
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const avgRejectRate = totalLeads > 0 ? Math.round(data.reduce((s, d) => s + d.rejected, 0) / totalLeads * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Leads" value={totalLeads} icon={Users} link="/lead-integrity" />
        <MetricCard label="Revenue by Source" value={`$${(totalRevenue / 1000).toFixed(0)}k`} icon={DollarSign} color="text-success" />
        <MetricCard label="Avg Rejection Rate" value={`${avgRejectRate}%`} icon={Ban} color={avgRejectRate > 20 ? "text-destructive" : "text-warning"} />
        <MetricCard label="Active Sources" value={data.length} icon={Megaphone} link="/lead-sources" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Performance by Source</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Source</TableHead>
                <TableHead className="text-xs text-right">Leads</TableHead>
                <TableHead className="text-xs text-right">Reject %</TableHead>
                <TableHead className="text-xs text-right">Quotes</TableHead>
                <TableHead className="text-xs text-right">Conv %</TableHead>
                <TableHead className="text-xs text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.slice(0, 10).map(d => (
                <TableRow key={d.source}>
                  <TableCell className="text-xs font-medium">
                    <Link to="/lead-sources" className="text-primary hover:underline">{d.source}</Link>
                  </TableCell>
                  <TableCell className="text-xs text-right">{d.total}</TableCell>
                  <TableCell className={`text-xs text-right ${d.rejectRate > 30 ? "text-destructive" : ""}`}>{d.rejectRate}%</TableCell>
                  <TableCell className="text-xs text-right">{d.quotes}</TableCell>
                  <TableCell className={`text-xs text-right ${d.convRate > 40 ? "text-success" : ""}`}>{d.convRate}%</TableCell>
                  <TableCell className="text-xs text-right">${d.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SALES PERFORMANCE
   ═══════════════════════════════════════════════════════════════ */

export function SalesPerformance() {
  const { quotes, leads } = useAppData();

  const repData = useMemo(() => {
    const reps = new Map<string, {
      leads: number; contacted: number; quoted: number; won: number; lost: number;
      revenue: number; totalEstimate: number;
    }>();

    leads.forEach(l => {
      const rep = l.assignedRep || "Unassigned";
      const e = reps.get(rep) || { leads: 0, contacted: 0, quoted: 0, won: 0, lost: 0, revenue: 0, totalEstimate: 0 };
      e.leads++;
      if (l.stage !== "Lead") e.contacted++;
      reps.set(rep, e);
    });

    quotes.forEach(q => {
      const rep = q.assignedRep || q.createdBy || "Unknown";
      const e = reps.get(rep) || { leads: 0, contacted: 0, quoted: 0, won: 0, lost: 0, revenue: 0, totalEstimate: 0 };
      e.quoted++;
      e.totalEstimate += q.totalEstimate || 0;
      if (isWonStatus(q.status)) { e.won++; e.revenue += q.totalEstimate || 0; }
      if (q.status === "Closed Lost") e.lost++;
      reps.set(rep, e);
    });

    return [...reps.entries()].map(([rep, d]) => ({
      rep,
      repDisplay: resolveProfileDisplayName(rep, undefined, "Unassigned"),
      ...d,
      contactRate: d.leads > 0 ? Math.round((d.contacted / d.leads) * 100) : 0,
      quoteRate: d.contacted > 0 ? Math.round((d.quoted / d.contacted) * 100) : 0,
      closeRate: d.quoted > 0 ? Math.round((d.won / d.quoted) * 100) : 0,
      avgDeal: d.won > 0 ? d.revenue / d.won : 0,
    })).filter(d => d.repDisplay !== "Unassigned").sort((a, b) => b.revenue - a.revenue);
  }, [quotes, leads]);

  const totals = useMemo(() => ({
    leads: repData.reduce((s, d) => s + d.leads, 0),
    quoted: repData.reduce((s, d) => s + d.quoted, 0),
    won: repData.reduce((s, d) => s + d.won, 0),
    revenue: repData.reduce((s, d) => s + d.revenue, 0),
    avgClose: repData.length > 0 ? Math.round(repData.reduce((s, d) => s + d.closeRate, 0) / repData.length) : 0,
  }), [repData]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Leads" value={totals.leads} icon={Users} link="/pipeline" />
        <MetricCard label="Quotes Created" value={totals.quoted} icon={BarChart3} link="/opportunities" />
        <MetricCard label="Avg Close Rate" value={`${totals.avgClose}%`} icon={Target} color={totals.avgClose > 30 ? "text-success" : "text-warning"} />
        <MetricCard label="Revenue Closed" value={`$${(totals.revenue / 1000).toFixed(0)}k`} icon={TrendingUp} color="text-success" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Sales Rep Performance</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Rep</TableHead>
                <TableHead className="text-xs text-right">Leads</TableHead>
                <TableHead className="text-xs text-right">Contact %</TableHead>
                <TableHead className="text-xs text-right">Quotes</TableHead>
                <TableHead className="text-xs text-right">Close %</TableHead>
                <TableHead className="text-xs text-right">Avg Deal</TableHead>
                <TableHead className="text-xs text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {repData.map(d => (
                <TableRow key={d.rep}>
                  <TableCell className="text-xs font-medium">{d.repDisplay}</TableCell>
                  <TableCell className="text-xs text-right">{d.leads}</TableCell>
                  <TableCell className="text-xs text-right">{d.contactRate}%</TableCell>
                  <TableCell className="text-xs text-right">{d.quoted}</TableCell>
                  <TableCell className={`text-xs text-right ${d.closeRate > 40 ? "text-success font-medium" : d.closeRate < 20 ? "text-destructive" : ""}`}>{d.closeRate}%</TableCell>
                  <TableCell className="text-xs text-right">${d.avgDeal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</TableCell>
                  <TableCell className="text-xs text-right font-medium">${d.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Performance Chart */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Rep</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={repData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="repDisplay" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
              <Bar dataKey="revenue" fill={C.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   PIPELINE INTELLIGENCE LINK CARD (no import / no embed / no fetch)
   ═══════════════════════════════════════════════════════════════ */
function PipelineIntelligenceLinkCard() {
  return (
    <Card className="border-dashed hover:border-primary/40 transition-colors">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Activity className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Pipeline Intelligence</p>
          <p className="text-xs text-muted-foreground">Forecasting & funnel scoring (Beta)</p>
        </div>
        <Link
          to="/pipeline-intelligence"
          className="text-xs font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5"
        >
          Open Pipeline Intelligence <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default function IntelligenceDashboard() {
  const [tab, setTab] = useState("ops");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-bold tracking-tight">Intelligence & Reporting</h1>
        <p className="text-xs text-muted-foreground">Cross-functional risk and governance signals</p>
      </div>

      <PipelineIntelligenceLinkCard />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-0.5 p-1">
          <TabsTrigger value="ops" className="text-xs gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Operational Risk</TabsTrigger>
          <TabsTrigger value="overrides" className="text-xs gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> Override Intelligence</TabsTrigger>
        </TabsList>

        <TabsContent value="ops" className="mt-4"><OperationalRisk /></TabsContent>
        <TabsContent value="overrides" className="mt-4"><OverrideIntelligence /></TabsContent>
      </Tabs>
    </div>
  );
}
